// Wraps node:child_process.spawn with detached:true (own process group),
// stdio all piped, injectable `deps.spawn` (NOT vi.mock — tests use
// FakeClaudeChild via the injected function, per phase spec preference).
//
// P01 crash-safety fix: a spawn failure (no pid) resolves to
// `{ok:false, code}` instead of throwing — callers must check `.ok` before
// touching `.child`/`.pid`/`.kill`. A child that DID get a pid but later
// emits 'error' is only recorded, never re-thrown.
//
// Kill sequence: TERM group -> POLL (not blind-sleep) up to graceMs, exiting
// EARLY the moment the group is reaped -> KILL group (only if still alive
// after grace) -> poll up to 5s more -> resolves groupReaped:true.
//
// P9b fix: session-runner.ts calls `kill()` UNCONDITIONALLY at the end of
// EVERY attempt (its own comment: "regardless of which branch...natural
// exit, startup_timeout, or deadline"), not only when a process is stuck.
// The ORIGINAL sequence did `await deps.sleep(graceMs)` unconditionally
// between TERM and KILL — for the overwhelmingly common case (the worker
// already exited on its own, `waitForExitOrDeadline` resolved via the real
// "exit" event), that blind sleep wasted the FULL `shutdown_grace_sec`
// (config default 20s) on a process that was already gone, on EVERY single
// attempt — proven via a real CLI E2E: a 5-ticket mission took >100s wall
// time (~20s/ticket) purely from this dead sleep, despite each attempt's
// actual work completing in well under a second. Polling during the grace
// window (same POLL_INTERVAL_MS this file already uses for the post-KILL
// reap wait) preserves the exact contract a genuinely stuck process needs
// (up to graceMs before SIGKILL — test_process_group_kill_reaps_child_
// ignoring_sigterm_and_its_grandchild still exercises that path) while
// letting the already-exited case return in ~one poll tick instead of the
// full window.
import type { ChildProcess, spawn as nodeSpawn, SpawnOptions } from "node:child_process";
import { terminateProcessGroup } from "./process-group-reaping.js";

export type SpawnFn = typeof nodeSpawn;

export interface ProcessGroupSpawnerDeps {
  spawn: SpawnFn;
  sleep: (ms: number) => Promise<void>;
}

export interface SpawnedGroup {
  ok: true;
  child: ChildProcess;
  pid: number;
  pgid: number;
  kill: (graceMs: number) => Promise<{ groupReaped: boolean }>;
}

// How long to wait for Node's 'error' event after a spawn that never got a
// pid (spawn failed synchronously-ish; Node still emits 'error' on the next
// tick, never synchronously) before giving up and reporting an unknown code.
const SPAWN_ERROR_WAIT_MS = 1000;

function errnoCode(err: unknown): string {
  const code = (err as NodeJS.ErrnoException)?.code;
  return typeof code === "string" && code.length > 0 ? code : "unknown";
}

// Real timer (NOT deps.sleep): a stubbed sleep that resolves immediately
// would win the race against the 'error' event in tests, always producing
// "unknown" instead of the real errno.
function waitForSpawnErrorCode(child: ChildProcess): Promise<string> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve("unknown"), SPAWN_ERROR_WAIT_MS);
    timer.unref();
    child.once("error", (err) => {
      clearTimeout(timer);
      resolve(errnoCode(err));
    });
  });
}

export async function spawnClaudeProcessGroup(
  command: string,
  args: string[],
  options: SpawnOptions,
  deps: ProcessGroupSpawnerDeps,
): Promise<SpawnedGroup | { ok: false; code: string }> {
  const child = deps.spawn(command, args, {
    ...options,
    detached: true,
    stdio: ["pipe", "pipe", "pipe"],
  });
  // F2: always attach a listener so a later 'error' (e.g. EPIPE once the
  // child already has a pid) never throws past this EventEmitter — the
  // attempt's outcome is decided by exit/deadline handling downstream, not
  // by this event.
  child.on("error", () => {
    /* observed only to prevent Node's default unhandled-'error' throw */
  });

  if (child.pid === undefined) {
    return { ok: false, code: await waitForSpawnErrorCode(child) };
  }
  const pid = child.pid;
  const pgid = pid; // detached:true => child is its own process group leader, pgid === pid (POSIX)

  const kill = (graceMs: number): Promise<{ groupReaped: boolean }> =>
    terminateProcessGroup(pgid, graceMs, deps.sleep);

  return { ok: true, child, pid, pgid, kill };
}
